"use client";

import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Grid, IconButton, MenuItem, Paper, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  Checkbox, FormGroup, FormLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert } from "../../helper";

const MODULES = [
  "Shift Management",
  "Deposit",
  "Chip Issue",
  "Chip Cash-Out",
  "Withdrawal",
  "Chip Exchange",
  "Float Exchange",
  "Shift Ledger",
  "Session Manager",
  "Transaction",
  "Rake Manager",
  "Tip Manager",
  "Credit Manager",
  "Expense Manager",
  "Petty Expense",
  "Chips Master",
  "Wallet Manager",
  "User Manager",
  "Telegram Cashier",
  "Employee Manager",
  "Bad Beat Jackpot",
  "Account Manager",
  "Player Manager",
  "Settings",
  "Dashboard",
];

const ROLES = [
  { label: "Admin",     value: 100 },
  { label: "Sub Admin", value: 80  },
  { label: "Cashier",   value: 50  },
];

const emptyForm = { name: "", email: "", password: "", role: 80, modules: [], status: true };

export default function UserManager() {
  const token = useSelector((state) => state.loginSlice.token);

  const [users, setUsers]         = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);

  const fetchUsers = () =>
    axiosRequest("GET", `${apiBaseUrl}/user`, token).then((res) =>
      setUsers(Array.isArray(res?.data) ? res.data : [])
    );

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (user) => {
    const modules = (user.permissions || []).map((p) => p.module || p).filter(Boolean);
    setEditId(user._id);
    setForm({
      name:     user.name    || "",
      email:    user.email   || "",
      password: "",
      role:     user.access_level ?? 80,
      modules,
      status:   user.status ?? true,
    });
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setEditId(null); setForm(emptyForm); };

  const toggleModule = (mod) => {
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(mod)
        ? f.modules.filter((m) => m !== mod)
        : [...f.modules, mod],
    }));
  };

  const selectAll = () => setForm((f) => ({ ...f, modules: [...MODULES] }));
  const clearAll  = () => setForm((f) => ({ ...f, modules: [] }));

  const handleSave = async () => {
    if (!form.name || !form.email) { getSweetAlert("error", "Name and email are required"); return; }
    if (!editId && !form.password)  { getSweetAlert("error", "Password is required for new users"); return; }

    setSaving(true);
    const payload = {
      name:         form.name,
      email:        form.email,
      access_level: Number(form.role),
      status:       form.status,
      permissions:  form.modules.map((m) => ({ module: m })),
      ...(form.password ? { password: form.password } : {}),
    };

    const res = editId
      ? await axiosRequest("PATCH", `${apiBaseUrl}/user/${editId}`, token, payload)
      : await axiosRequest("POST",  `${apiBaseUrl}/user`,           token, payload);

    setSaving(false);

    if (res?.data?._id || res?.data?.status !== false) {
      getSweetAlert("success", editId ? "User updated" : "User created");
      handleClose();
      fetchUsers();
    } else {
      getSweetAlert("error", res?.data?.message || res?.message || "Failed to save");
    }
  };

  const roleName = (level) => ROLES.find((r) => r.value === level)?.label || "—";

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>User Manager</Typography>
          <Typography color="text.secondary">Manage admin accounts and module access</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
          Add User
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ background: "#f8f9fc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Modules</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const modules = (user.permissions || []).map((p) => p.module || p).filter(Boolean);
                return (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{user.name}</Typography>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={roleName(user.access_level)}
                        color={user.access_level >= 100 ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {modules.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">All access</Typography>
                        ) : (
                          modules.slice(0, 3).map((m) => (
                            <Chip key={m} label={m} size="small" variant="outlined" />
                          ))
                        )}
                        {modules.length > 3 && (
                          <Chip label={`+${modules.length - 3}`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status ? "Active" : "Inactive"}
                        color={user.status ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => openEdit(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editId ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Full Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label={editId ? "New Password (leave blank to keep)" : "Password"}
                type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Role" value={form.role}
                onChange={(e) => setForm({ ...form, role: Number(e.target.value) })}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.status} onChange={(e) => setForm({ ...form, status: e.target.checked })} />}
                label={form.status ? "Active" : "Inactive"}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <FormLabel sx={{ fontWeight: 700, color: "text.primary" }}>Module Access</FormLabel>
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={selectAll}>Select All</Button>
                  <Button size="small" color="error" onClick={clearAll}>Clear</Button>
                </Box>
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <FormGroup row>
                  {MODULES.map((mod) => (
                    <FormControlLabel
                      key={mod}
                      sx={{ width: "50%", m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.modules.includes(mod)}
                          onChange={() => toggleModule(mod)}
                        />
                      }
                      label={<Typography variant="body2">{mod}</Typography>}
                    />
                  ))}
                </FormGroup>
              </Paper>
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Admin role ignores module restrictions — sub admins are limited to selected modules only.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2 }}>
            {saving ? "Saving…" : editId ? "Update User" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
